import React, { useState, useEffect } from 'react';
import {
  Layout,
  Typography,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Spin,
  message,
} from 'antd';
import { DeleteFilled, EditFilled, ExclamationCircleOutlined, CopyOutlined } from '@ant-design/icons';
import notification from '../../Global/Notification';
import  Constant  from '../../Global/Constant';
import AddAppModal from './AddAppModal';
import appStore from '../../Store/appStore';

const { Search } = Input;

function App() {
  const [isModalVisible, setModalVisibility] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  
  const [form] = Form.useForm();

  const viewModal = (isEditMode:boolean, record?: any) => {
    setEditMode(isEditMode);
    form.resetFields();
    form.setFieldsValue(record || {});
    form.setFields([
      { touched: false, name: 'id' },
      { touched: false, name: 'appName' },
      { touched: false, name: 'callbackUrl' },
      { touched: false, name: 'domains' },
      { touched: false, name: 'userRoles' },
      { touched: false, name: 'userDetails' },
      { touched: false, name: 'appId' },
      { touched: false, name: 'active' },
    ]);
    setModalVisibility(true);
  };

  const getApps = ( currentPageNumber: number, searchValue?: string) => {
    setListLoading(true);
    appStore.getAppList((error: string) => {
      setListLoading(false);
      if (error) {
        notification.error({
          description: error,
          message: 'Error',
        });
      } else {
        setPageNumber(currentPageNumber);
      }
    }, currentPageNumber, Constant.itemsPerPage, searchValue,);
  };

  useEffect(() => {
    getApps(pageNumber);
    // eslint-disable-next-line
  }, []);

  const onPageChange = (currentPageNo: number) => {
    getApps(currentPageNo, searchText);
  };

  const saveDataAction = (err: string) => {
    setLoading(false);
    if (err) {
      notification.error({
        description: err,
        message: 'Error',
      });
    } else {
      setModalVisibility(false);
      getApps(
        editMode ? pageNumber : Math.ceil((appStore.total + 1) / Constant.itemsPerPage),

        searchText,
      );
    }
  };

  const generateAppId = () => {
    const ids = form.getFieldValue('id');         
    appStore.updateAppId(ids, (err: any, data?: string) => {
      if (err) {
        notification.error({
          description: err,
          message: 'Error',
        });
      } else {
        data && form.setFieldsValue({'appId': data});
      }
    });
    message.info('AppId Changed!');
  }

  const generateKey = (id? :any, key?: any) => {
    if(id && key) {
      
      var element = document.createElement('a');
      element.setAttribute(
        'href',
        'data:text/plain;charset=utf-8,' + encodeURIComponent(key)
      );
      element.setAttribute('download', `${id}_privateKey.pem`);
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();     
      message.info('Downloaded PEM file');

    }else {
      const ids = form.getFieldValue('id');
      appStore.updateKey(ids, (err: any, data?: any) => {
        if (data) {
          var element = document.createElement('a');
          element.setAttribute(
            'href',
            'data:text/plain;charset=utf-8,' + encodeURIComponent(data?.key)
          );
          element.setAttribute('download', `${data?.id}_privateKey.pem`);
          element.style.display = 'none';
          document.body.appendChild(element);
          element.click();
        }
        setListLoading(false);
        message.info('Downloaded PEM file');
      });
    }
  };

  const saveAppData = () => {
    form
      .validateFields()
      .then((values) => {
        setLoading(true);
        const {
          appName, callbackUrl, domains, userRoles, userDetails
        } = values;
        if (!values.id) {
          appStore.addApp(appName, callbackUrl, domains, userRoles, userDetails, (err: any, data?: any) => {
            saveDataAction(err);
            generateKey(data.id, data.key)
          });
          
        } else {
          const updatedValues = form.getFieldsValue(Object.keys(values)
            .filter((x: string) => form.isFieldTouched(x)));
          if (Object.keys(updatedValues).length) {
            appStore.updateApp(values.id, updatedValues, (err: any) => {
              saveDataAction(err);
            });
          } else {
            setLoading(false);
            setModalVisibility(false);
          }
        }
      })
      .catch(() => {});
  };

  const deleteApp = (id:string) => {
    setLoading(true);
    appStore.deleteApp(id, (error: string) => {
      setLoading(false);
      if (error) {
        notification.error({
          description: error,
          message: 'Error',
        });
      } else {
        const lastPageNumber = Math.ceil((appStore.total - 1) / Constant.itemsPerPage);
        getApps(
          pageNumber < lastPageNumber ? pageNumber : lastPageNumber,
          searchText,
        );
      }
    });
  }

  const confirmDeletion = (id : string) => {
    Modal.confirm({
      title: Constant.deleteModalTitle,
      icon: <ExclamationCircleOutlined />,
      content: Constant.deleteModalContent,
      okText: 'Yes',
      cancelText: 'No',
      onOk() { deleteApp(id); },
    });
  };

  const copyToClipboard = (appId: any) => {
    navigator?.clipboard?.writeText(appId);
    message.info('Copied AppId!');
  }

  const tableColumns = [
    {
      title: '#',
      key: 'key',
      render: (record: any) => (
        <span>
          { (
            appStore.app.indexOf(record) + 1)
            + (Constant.itemsPerPage * (pageNumber - 1)
            ) }
        </span>
      ),
    },
    {
      title: 'App Name',
      dataIndex: 'appName',
      key: 'appName',
    },
    {
      title: 'App Id',
      dataIndex: 'appId',
      key: 'appId',
    },
    {
      key: 'action',
      render: (text: any, record: any) => (
        <Space size="large">
          <CopyOutlined onClick={()=> copyToClipboard(record.appId)}/>
          <EditFilled onClick={() => viewModal(true, record)} />
          <DeleteFilled onClick={() => confirmDeletion(record.id)} />
        </Space>
      ),
    },
  ] as any;

  const onSearchText = (currentSearchText: string) => {
    if (currentSearchText !== searchText) {
      setSearchText(currentSearchText);
    }
    getApps(Constant.defaultPageNumber, currentSearchText);
  };

  const cancelModal = () => {
    setModalVisibility(false);
    const lastPageNumber = Math.ceil((appStore.total - 1) / Constant.itemsPerPage);
    getApps(
      pageNumber < lastPageNumber ? pageNumber : lastPageNumber,
      searchText,
    );
  };

  const handleSearchTextChange = (value: string) => {
    if (value.length === 0) {
      onSearchText('');
    }
  };

  return (
    <Layout.Content>
      <Spin spinning={listLoading}>
<div className="container">
        <div className='d-flex flex-column flex-sm-row mb-3'>
          <div className='flex-fill content-title mb-3'>
            <Typography.Title level={4}>
                App List
            </Typography.Title>
            <div className='title-line'></div>
          </div>
          <div className='flex-auto'>
            <div className='top-bar d-flex mb-3'>
              <div className='d-inline-block mr-2 pr-2 border-right'>
                <Search
                    placeholder="Search"
                    className="ml-2"
                    onSearch={(value) => onSearchText(value)}
                    onChange={(e) => handleSearchTextChange(e.target.value)}
                    style={{ width: 343 }}
                    allowClear
                />
              </div>
              <div className='d-inline-block add-btn-style'>
                <Button
                  className='sm'
                  type='primary'
                  onClick={() => viewModal(false)}
                  disabled={loading}
                >
                  Add
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="position-relative sso-table">
          <Table
            className={`bg-white ${appStore.total <= Constant.itemsPerPage ? 'table-padding' : ''}`}
            columns={tableColumns}
            dataSource={appStore.app}
            pagination={appStore.total > Constant.itemsPerPage
              && {
                pageSize: Constant.itemsPerPage,
                position: ['bottomCenter'],
                total: appStore.total,
                current: pageNumber,
                onChange: onPageChange,
                showSizeChanger: false,
              }}
          />
        </div>

        <AddAppModal
          editMode={editMode}
          form={form}
          isModalVisible={isModalVisible}
          loading={loading}
          onSave={saveAppData}
          onCancel={() => cancelModal()}
          generateAppId={generateAppId}
          generateKey={generateKey}
        />
        </div>
      </Spin>
    </Layout.Content>
  );
}

export default App;
